/**
 * CloudWatch Connections List
 *
 * Unified table showing both Bedrock and SageMaker connections with a source type indicator.
 */

import { useState } from "react";
import { Trash2, RefreshCw, Clock, AlertCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useBedrockConnections,
  useDeleteBedrockConnection,
  useSyncBedrockConnection,
} from "@/hooks/useBedrock";
import {
  useSageMakerConnections,
  useDeleteSageMakerConnection,
  useSyncSageMakerConnection,
} from "@/hooks/useSagemaker";
import type { BedrockConnectionRead } from "@/types/bedrock";
import type { SageMakerConnectionRead } from "@/types/sagemaker";

type SourceType = "bedrock" | "sagemaker";

interface UnifiedConnection {
  id: number;
  source: SourceType;
  connection_name: string;
  log_group_name: string;
  aws_region: string;
  is_active: boolean;
  consecutive_errors: number;
  last_sync_at: string | null;
  last_error: string | null;
}

function toUnified(conn: BedrockConnectionRead, source: "bedrock"): UnifiedConnection;
function toUnified(conn: SageMakerConnectionRead, source: "sagemaker"): UnifiedConnection;
function toUnified(
  conn: BedrockConnectionRead | SageMakerConnectionRead,
  source: SourceType
): UnifiedConnection {
  return { ...conn, source };
}

function getStatusBadge(conn: UnifiedConnection) {
  if (!conn.is_active) {
    return (
      <Badge variant="secondary" className="gap-1">
        <AlertCircle className="h-3 w-3" />
        Inactive
      </Badge>
    );
  }
  if (conn.consecutive_errors > 0) {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertCircle className="h-3 w-3" />
        Error
      </Badge>
    );
  }
  return (
    <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-700">
      <CheckCircle2 className="h-3 w-3" />
      Active
    </Badge>
  );
}

function SourceBadge({ source }: { source: SourceType }) {
  return source === "bedrock" ? (
    <Badge variant="outline" className="text-xs font-mono">
      Bedrock
    </Badge>
  ) : (
    <Badge variant="outline" className="text-xs font-mono">
      SageMaker
    </Badge>
  );
}

export function CloudWatchConnectionsList() {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<UnifiedConnection | null>(null);
  const [syncingKey, setSyncingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: bedrockData, isLoading: bedrockLoading } = useBedrockConnections(1, 100);
  const { data: sagemakerData, isLoading: sagemakerLoading } = useSageMakerConnections(1, 100);

  const deleteBedrockMutation = useDeleteBedrockConnection();
  const deleteSageMakerMutation = useDeleteSageMakerConnection();
  const syncBedrockMutation = useSyncBedrockConnection();
  const syncSageMakerMutation = useSyncSageMakerConnection();

  const bedrockConnections = (bedrockData?.data ?? []).map((c) => toUnified(c, "bedrock"));
  const sagemakerConnections = (sagemakerData?.data ?? []).map((c) => toUnified(c, "sagemaker"));
  const connections = [...bedrockConnections, ...sagemakerConnections];

  const connKey = (conn: UnifiedConnection) => `${conn.source}-${conn.id}`;
  const isBusy = (conn: UnifiedConnection) => {
    const key = connKey(conn);
    return syncingKey === key || deletingKey === key;
  };

  const formatDate = (dateString: string | null) =>
    dateString ? new Date(dateString).toLocaleString() : "Never";

  const handleSyncClick = async (conn: UnifiedConnection) => {
    if (isBusy(conn)) return;
    setActionError(null);
    const key = connKey(conn);
    setSyncingKey(key);
    try {
      if (conn.source === "bedrock") {
        await syncBedrockMutation.mutateAsync(conn.id);
      } else {
        await syncSageMakerMutation.mutateAsync(conn.id);
      }
    } catch (error) {
      setActionError(
        `Failed to sync "${conn.connection_name}": ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setSyncingKey(null);
    }
  };

  const handleDeleteClick = (conn: UnifiedConnection) => {
    if (isBusy(conn)) return;
    setConnectionToDelete(conn);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!connectionToDelete || deletingKey) return;
    setActionError(null);
    const key = connKey(connectionToDelete);
    setDeletingKey(key);
    try {
      if (connectionToDelete.source === "bedrock") {
        await deleteBedrockMutation.mutateAsync(connectionToDelete.id);
      } else {
        await deleteSageMakerMutation.mutateAsync(connectionToDelete.id);
      }
      setDeleteDialogOpen(false);
      setConnectionToDelete(null);
    } catch (error) {
      setActionError(
        `Failed to delete "${connectionToDelete.connection_name}": ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setDeletingKey(null);
    }
  };

  const isLoading = bedrockLoading || sagemakerLoading;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted p-3 mb-4">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-lg mb-1">No Connections Configured</h3>
        <p className="text-sm text-muted-foreground">
          Add your first AWS CloudWatch connection to start ingesting model invocation logs
        </p>
      </div>
    );
  }

  return (
    <>
      {actionError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200 mb-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{actionError}</span>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Connection</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Last Sync</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {connections.map((conn) => (
            <TableRow key={connKey(conn)}>
              <TableCell className="font-medium">
                <div>
                  <div>{conn.connection_name}</div>
                  <div className="text-xs text-muted-foreground truncate max-w-xs">
                    {conn.log_group_name}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <SourceBadge source={conn.source} />
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  {getStatusBadge(conn)}
                  {conn.last_error && (
                    <div className="text-xs text-destructive line-clamp-1">{conn.last_error}</div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <span className="text-sm font-mono">{conn.aws_region}</span>
              </TableCell>
              <TableCell>
                <div className="text-sm">{formatDate(conn.last_sync_at)}</div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSyncClick(conn)}
                    disabled={isBusy(conn)}
                    title={`Sync ${conn.connection_name} now`}
                    aria-label={`Sync ${conn.connection_name} now`}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${syncingKey === connKey(conn) ? "animate-spin" : ""}`}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(conn)}
                    disabled={isBusy(conn)}
                    className="text-destructive hover:text-destructive"
                    title={`Delete ${conn.connection_name}`}
                    aria-label={`Delete ${conn.connection_name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open && deletingKey) return;
          setDeleteDialogOpen(open);
          if (!open) setConnectionToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Connection</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{connectionToDelete?.connection_name}"? Ingestion
              will stop immediately. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={!!deletingKey}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={!!deletingKey}>
              {deletingKey ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
